'use client';

import { useContext, useEffect, useState, useSyncExternalStore } from 'react';

import { useRouter } from 'next/navigation';

import { isGuestUser } from '@ps/functions/auth/isGuestUser';
import { writeLoginBrandHandoff } from '@ps/functions/auth/loginBrandHandoff';
import type { CreatorInfo } from '@ps/types/creator';

import { AuthContext } from './context';
import {
  type LoginSuccessCallback,
  consumeLoginSuccessCallback,
  setLoginSuccessCallback,
} from './loginCallback';

export * from './context';
export {
  type LoginSuccessCallback,
  consumeLoginSuccessCallback,
  setLoginSuccessCallback,
};

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export function useCurrentUserId(): string | null | undefined {
  const { currentUserId$ } = useAuthContext();

  return useSyncExternalStore(
    callback => {
      const subscription = currentUserId$.subscribe(callback);
      return () => subscription.unsubscribe();
    },
    () => currentUserId$.getValue(),
    // Server/hydration snapshot MUST be `undefined` (not `null`) to match the
    // provider's initial `BehaviorSubject(undefined)`. `undefined` means "auth
    // still resolving"; `null` means "resolved, no user". Returning `null` here
    // would make a logged-in user look anonymous on the first client render —
    // fatal for auth gates that branch on `=== null` (user-access, DmGate) and
    // for redirect-on-signed-out side effects.
    () => undefined,
  );
}

/**
 * Whether a REAL (non-guest) user is signed in. A throwaway guest account
 * (created during guest checkout — the `isGuest` custom claim) has a uid, so
 * `useCurrentUserId() != null` treats it as logged in; this does NOT. Resolves
 * the claim from the current user's token asynchronously, so it reads `false`
 * (logged-out) while auth is still resolving or the claim is in flight — the
 * correct default for a login/account affordance.
 */
export function useIsSignedInNonGuest(): boolean {
  const { currentUser$ } = useAuthContext();
  const [signedInNonGuest, setSignedInNonGuest] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const subscription = currentUser$.subscribe(user => {
      if (!user) {
        setSignedInNonGuest(false);
        return;
      }
      isGuestUser(user)
        .then(guest => {
          if (!cancelled) {
            setSignedInNonGuest(!guest);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setSignedInNonGuest(false);
          }
        });
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [currentUser$]);

  return signedInNonGuest;
}

/**
 * TRI-STATE guest status of the current user: `undefined` while it's still
 * indeterminate (auth resolving, OR the `isGuest` claim in flight for a present
 * user), `true` for a throwaway guest account (the `isGuest` claim), `false` for a
 * real signed-in user OR nobody.
 *
 * Unlike {@link useIsSignedInNonGuest} (a boolean that reads `false` while
 * resolving), this DISTINGUISHES "still resolving" from "confirmed not a guest", so
 * an auth gate can wait instead of misfiring on a real user whose claim hasn't
 * loaded yet. Pair with {@link useCurrentUserId} for the anonymous case: a viewer
 * needs login when `useCurrentUserId() === null` (anonymous) OR `useIsGuestUser() === true`
 * (guest) — both `undefined` states mean "still resolving, don't act".
 */
export function useIsGuestUser(): boolean | undefined {
  const { currentUser$ } = useAuthContext();
  const [isGuest, setIsGuest] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const subscription = currentUser$.subscribe(user => {
      if (user === undefined) {
        setIsGuest(undefined); // auth still resolving
        return;
      }
      if (user === null) {
        setIsGuest(false); // nobody signed in — not a guest
        return;
      }
      setIsGuest(undefined); // a user is present, but its claim is in flight
      isGuestUser(user)
        .then(guest => {
          if (!cancelled) setIsGuest(guest);
        })
        .catch(() => {
          if (!cancelled) setIsGuest(false);
        });
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [currentUser$]);

  return isGuest;
}

/**
 * Build the app `/login` URL with a `next` return target. `next` defaults to the
 * CURRENT location (`pathname + search`) so the login flow returns the viewer
 * where they were. Client-only (reads `window.location`) — call it from event
 * handlers / effects, never during server render.
 */
export function buildLoginUrl(
  next?: string,
  defaultUserType?: 'creator' | 'member',
): string {
  const target = next ?? window.location.pathname + window.location.search;
  // `next` encoding is unchanged from before (`encodeURIComponent`). Only append
  // `as=member` for the non-default toggle — the login modal defaults to
  // "Creator or Business"; a restricted clip/post/course passes 'member' to open
  // on "Community Member" instead. Keeps ordinary login URLs clean.
  const suffix = defaultUserType === 'member' ? '&as=member' : '';
  return `/login?next=${encodeURIComponent(target)}${suffix}`;
}

export interface UseLoginParams {
  /** Post-login return target; defaults to the current location. */
  next?: string;
  /**
   * Imperative post-login continuation. It's set as the login-success callback,
   * so the intercepted `@login/(.)login` resumes the action on its soft
   * `router.back()`. In-memory (lost on refresh) — the durable fallback is the
   * `next` URL, which a cold-load login still honors.
   */
  onSuccess?: LoginSuccessCallback;
  /**
   * Creator brand for the login modal. When set, its `CreatorInfo` is written to
   * the login-brand handoff just before navigating, so the intercepted `/login`
   * renders "signing in to <creator>" chrome (avatar + name + the creator's own
   * privacy policy + any customized member copy). Omit for the bare form.
   */
  creatorInfo?: CreatorInfo;

  /**
   * Which login-toggle pill the modal opens on. Defaults to "Creator or
   * Business"; pass `'member'` from a restricted clip / post / course so the
   * modal opens on "Community Member" instead (encoded as `?as=member`).
   */
  defaultUserType?: 'creator' | 'member';

  /**
   * Whether to replace the current history entry (default `false`). When `true`,
   * the login modal's `router.back()` returns to the page that opened it, not the
   * login route itself. This is the common case for a modal opened from a page
   * (bio, checkout, etc.). When `false`, the login modal's `router.back()` lands
   * on the login route itself — useful for a standalone `/login` page that
   * should return to itself on cancel.
   */
  replace?: boolean;

  /** Whether to navigate with router or window.location. */
  method?: 'soft' | 'hard';
}

/**
 * Returns a `login(overrides?)` trigger that (1) registers the optional
 * `onSuccess` continuation and (2) SOFT-navigates to the intercepted
 * `/login?next=...` modal. Soft nav keeps the launching surface mounted so the
 * post-login `router.back()` returns to it and fires `onSuccess`. Use this
 * instead of a raw `router.push('/login')` / `window.location` so every gate
 * shares one login entry point.
 *
 * Params can be bound at the hook (defaults) AND/OR passed per call — the
 * per-call `overrides` win. Bind at the hook for a fixed target; pass per call
 * when the continuation is built at interaction time (e.g. resume the tapped
 * action), which is the common case.
 */
export function useLogin(
  defaults: UseLoginParams = {},
): (overrides?: UseLoginParams) => void {
  const router = useRouter();
  return (overrides?: UseLoginParams) => {
    const onSuccess = overrides?.onSuccess ?? defaults.onSuccess;
    const next = overrides?.next ?? defaults.next;
    const creatorInfo = overrides?.creatorInfo ?? defaults.creatorInfo;
    const replace = overrides?.replace ?? defaults.replace;
    const method = overrides?.method ?? defaults.method ?? 'soft';
    const defaultUserType =
      overrides?.defaultUserType ?? defaults.defaultUserType;
    const loginUrl = buildLoginUrl(next, defaultUserType);
    if (creatorInfo) {
      writeLoginBrandHandoff(creatorInfo);
    }
    if (method === 'hard') {
      window.location.href = loginUrl;
      return;
    }
    if (onSuccess) {
      setLoginSuccessCallback(onSuccess);
    }
    if (replace) {
      router.replace(loginUrl);
    } else {
      router.push(loginUrl);
    }
  };
}
